import { BxfChatBubble } from '@blk/bxf-react-components';
import { BxfChatBubbleType, uuidv4 } from '@blk/bxf-web-components';
import styled from '@emotion/styled';
import { useRef } from 'react';

import { ServerMessage } from '../components';
import {
    CHAT_BUBBLE_DATA_LINK_EVENTS,
    ChatExchangeConstants,
} from '../constants';
import { useBubbleAnimations, useChatBlkStore } from '../hooks';
import { ChatBlkBaseService } from '../services';
import { QueryState } from '../types';
import { getKey } from '../utils';

interface ChatExchangeProps {
    hashId: string;
    state?: QueryState;
    onImgLoad?: () => void;
}
const UserBubbleContentContainer = styled.div`
    font-size: 1rem; // $space-s
    line-height: 24px; // $space-m
    font-weight: 700;
`;

const UserBubbleContent = styled.span`
    display: block;
    width: 100%;
    white-space: normal;
    word-wrap: break-word;
`;

const ChatExchangeContainer = styled.div`
    padding-top: 1rem;
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    animation: fadeIn 0.5s ease-out forwards;
    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

/**
 * ChatExchange component renders a single exchange between the user chat bubble and a server message.
 *
 * @param {ChatExchangeProps} props - The properties for the ChatExchange component.
 *
 * @returns {JSX.Element} The rendered ChatExchange component.
 */
const ChatExchange = ({
    hashId,
    onImgLoad,
    state,
}: ChatExchangeProps): JSX.Element => {
    const { queries } = useChatBlkStore();
    const query = queries.entries[hashId];
    const chatExchangeRef = useRef<HTMLDivElement | null>(null);
    const { isLast } = useBubbleAnimations(state, hashId, chatExchangeRef);

    if (state === QueryState.CANCELED) {
        return null;
    }

    const { userInitials } = ChatBlkBaseService.getInstance() || {};

    return (
        <ChatExchangeContainer
            id={getKey(hashId || uuidv4(), 'chat-exchange')}
            style={{ position: isLast ? 'sticky' : 'relative', top: '0' }}
            ref={chatExchangeRef}
            key={getKey(hashId, 'user')}
        >
            <BxfChatBubble
                type={BxfChatBubbleType.USER}
                userInitials={userInitials}
                dataLinkEvent={CHAT_BUBBLE_DATA_LINK_EVENTS}
            >
                <UserBubbleContentContainer
                    slot={ChatExchangeConstants.USER_BUBBLE_CONTENT_SLOT}
                >
                    <UserBubbleContent>{query.query}</UserBubbleContent>
                </UserBubbleContentContainer>
            </BxfChatBubble>

            <ServerMessage
                key={getKey(hashId, 'response')}
                query={query}
                onImgLoad={onImgLoad}
            />
        </ChatExchangeContainer>
    );
};

export default ChatExchange;
